import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Re-fetch every 60s so price/availability changes from the booking panel
// propagate without needing a full page refresh.
const REFRESH_INTERVAL_MS = 60_000;

// Visual demo: ?demo=badges overrides the first 4 same-season trips with
// PROMO / FEW LEFT / SOLD OUT / PAST states so we can preview the styles
// without touching real data. Strip param from URL by removing it manually.
function applyDemoOverrides(trips) {
  if (typeof window === "undefined") return trips;
  const params = new URLSearchParams(window.location.search);
  if (params.get("demo") !== "badges") return trips;
  if (!Array.isArray(trips) || trips.length === 0) return trips;

  const overrides = [
    {
      // Past
      is_past: true,
      available: false,
      is_disabled: true,
      is_sold_out: false,
      is_few_left: false,
      is_promo: false,
    },
    {
      // Promo
      is_promo: true,
      promo_price: Math.round((trips[0]?.price || 2999) * 0.85),
      min_price_30d: Math.round((trips[0]?.price || 2999) * 0.85),
      is_sold_out: false,
      is_few_left: false,
      is_past: false,
      is_disabled: false,
      available: true,
    },
    {
      // Few left
      is_few_left: true,
      available_reservations: 3,
      is_promo: false,
      is_sold_out: false,
      is_past: false,
      is_disabled: false,
      available: true,
    },
    {
      // Sold out
      is_sold_out: true,
      available_reservations: 0,
      available: false,
      is_disabled: true,
      is_few_left: false,
      is_promo: false,
      is_past: false,
    }
  ];

  // Pick the first 4 trips and apply the corresponding override.
  const targets = trips.slice(0, Math.min(4, trips.length));
  const overridden = trips.map((t) => {
    const idx = targets.indexOf(t);
    if (idx < 0) return t;
    return { ...t, ...overrides[idx] };
  });
  return overridden;
}

export function useTrips(season) {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    let timer = null;

    const load = (showLoader) => {
      if (showLoader) setLoading(true);
      return axios
        .get(`${API}/trips`, { params: season ? { season } : {} })
        .then((r) => {
          if (!active) return;
          setTrips(applyDemoOverrides(r.data.trips || []));
          setError(null);
        })
        .catch((e) => {
          if (!active) return;
          setError(e);
        })
        .finally(() => active && showLoader && setLoading(false));
    };

    load(true);
    timer = setInterval(() => load(false), REFRESH_INTERVAL_MS);

    // Re-fetch on tab focus so a parent flipping back to the tab sees fresh data.
    const onVisible = () => {
      if (document.visibilityState === "visible") load(false);
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      active = false;
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [season]);

  return { trips, loading, error };
}
